import * as React from "react";
import styled, { keyframes } from "styled-components";
import Game from "./Types/Game";
import Button from "./Button";
import ConnectionIndicator from "./ConnectionIndicator";
import moment from "moment";
import { isOldGame } from "./utils";

const pulse = keyframes`
	0% {
    background-color: beige;
    color: white;
	}

	50% {
    background-color: #b5b58f;
    color: white;
	}
`;

const StyledStartScreen = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding-top: 20px;
  text-align: center;
  font-size: 1.5rem;
`;

const Games = styled.div`
  display: grid;
  grid-gap: 10px;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  margin: 0 auto;
`;

const ProgressItem = styled.li`
  display: flex;
  align-items: center;
`;

const fillUpProgressBar = (value: number, max: number) => keyframes`
	0% {
    width: 0%;
	}

	100% {
    width: ${(value / max) * 100}%;
  }
`;

const Progress = styled.div<{ value: number; max: number }>`
  height: 8px;
  background: #b5b58f;
  display: inline-block;
  width: 100%;
  position: relative;
  margin-left: 5px;
  border-radius: 10px;
  margin-top: 3px;

  &:after {
    content: "";
    height: 8px;
    background: #80805c;
    position: absolute;
    border-radius: 10px;
    animation: ${({ value, max }) => fillUpProgressBar(value, max)} 0.8s linear
      forwards;
  }
`;

const GameBox = styled.div<{ isOver: boolean }>`
  padding: 20px;
  background: beige;
  color: black;
  border-radius: 10px;
  animation: ${pulse} 0.8s linear forwards;
  animation-iteration-count: 3;
  font-size: 1em;
  text-align: left;

  h2 {
    margin: 0px;
  }

  ul {
    list-style-type: none;
    padding: 0px;
  }

  ul li ul li {
    &:before {
      content: "- ";
    }
  }

  ${Button} {
    font-size: 0.7em;
  }

  ${({ isOver: isCancelled }) => isCancelled && `opacity: 0.5`}
`;

const SecretButton = styled.div`
  opacity: 0;
  width: 10px;
  height: 10px;
  position: absolute;
  top: 0px;
  left: 0px;
`;

interface Props {
  onJoinGame: (game: Game, name?: string) => void;
  onSpectateGame: (game: Game) => void;
  onCreateGame: () => void;
  onShowStatistics: () => void;
  games: Game[];
}

const StartScreen = ({
  onJoinGame,
  onShowStatistics,
  onSpectateGame,
  onCreateGame,
  games,
}: Props) => {
  return (
    <StyledStartScreen>
      <SecretButton onClick={onShowStatistics} />
      <h1>i'm closer than you</h1>

      <Games>
        {games && games.length > 0 && (
          <>
            {games
              .filter((g) => !isOldGame(g))
              .sort((a, b) => b.created - a.created)
              .map((game) => {
                const { friendlyId, created, players, state, rules, deck } =
                  game;

                return (
                  <GameBox
                    key={`game-${game.friendlyId}`}
                    isOver={game.state === "cancelled" || game.state === "lost"}
                  >
                    <h2>Game {friendlyId}</h2>
                    <ul>
                      <li>
                        <strong>created:</strong>{" "}
                        {moment(new Date(created), "YYYYMMDD").fromNow()}
                      </li>

                      <li>
                        <strong>state:</strong> {state}
                      </li>
                      <li>
                        <strong>mode:</strong> {rules}
                      </li>
                      {game.state !== "not started" && (
                        <ProgressItem>
                          <strong>Progress:</strong>
                          <Progress
                            value={deck ? 98 - deck.length : 0}
                            max={98}
                          ></Progress>
                        </ProgressItem>
                      )}
                      <li>
                        <br />
                        <strong>players:</strong>{" "}
                        {!players ||
                          (players && players.length === 0 && (
                            <span>no players yet</span>
                          ))}
                        <ul>
                          {players &&
                            players.length > 0 &&
                            players.map(({ name, isConnected }) => (
                              <li key={`player-${name}`}>
                                {name}{" "}
                                <ConnectionIndicator
                                  isConnected={isConnected}
                                />{" "}
                                {!isConnected && (
                                  <Button
                                    onClick={() => onJoinGame(game, name)}
                                    isSmall={true}
                                  >
                                    join as {name}
                                  </Button>
                                )}
                              </li>
                            ))}
                        </ul>
                      </li>
                    </ul>
                    {game.state === "not started" && (
                      <Button onClick={() => onJoinGame(game)}>
                        Join game
                      </Button>
                    )}
                    {game.state === "started" && (
                      <Button isDark onClick={() => onSpectateGame(game)}>
                        Spectate! 👀
                      </Button>
                    )}
                  </GameBox>
                );
              })}
          </>
        )}
      </Games>
      {!games ||
        (games && games.length === 0 && (
          <div>Looks like no games are running</div>
        ))}

      <br />
      <Button isBig onClick={onCreateGame}>
        Start a new game
      </Button>
    </StyledStartScreen>
  );
};

export default StartScreen;
